import {
  Editor,
  Element as SlateElement,
  Range,
  Text,
  Transforms,
  type Range as SlateRange
} from 'slate';

import type {Comment, CommentThread} from '../constants/ReportBuilderInterfce';

export type RawComment = Comment & {
  comment?: string;
  user_id?: string;
  updateAt?: string;
  mentions?: Array<{id: string; name: string; email?: string}>;
};

export const createCommentId = (): string =>
  crypto?.randomUUID
    ? `c_${crypto.randomUUID()}`
    : `c_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const createThreadId = (): string =>
  crypto?.randomUUID
    ? `thread_${crypto.randomUUID()}`
    : `thread_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const getCommentUserId = (comment: RawComment): string =>
  String(comment.user_id ?? comment.userId ?? '');

export const getCommentText = (comment: RawComment): string => {
  const legacy = comment.comment;
  if (typeof legacy === 'string' && legacy.trim().length > 0) return legacy;

  return comment.text ?? '';
};

export const getThreadIdFromComment = (comment: RawComment): string =>
  comment.threadId ?? comment.id;

export const isEmptyNewDraft = (comment: RawComment): boolean =>
  comment.isNew === true && getCommentText(comment).trim() === '';

export const getThreadIdsInRange = (
  editor: Editor,
  range: SlateRange
): string[] => {
  const ids = new Set<string>();
  Array.from(
    Editor.nodes(editor, {
      at: range,
      match: (n) => Text.isText(n)
    })
  ).forEach(([node]) => {
    const tid = (node as {commentThreadId?: string}).commentThreadId;
    if (typeof tid === 'string') ids.add(tid);
  });

  return Array.from(ids);
};

export const isRangeFullyInThread = (
  editor: Editor,
  range: SlateRange,
  threadId: string
): boolean => {
  let hasText = false;
  for (const [node] of Array.from(
    Editor.nodes(editor, {
      at: range,
      match: (n) => Text.isText(n)
    })
  )) {
    const textNode = node as {text: string; commentThreadId?: string};
    if (textNode.text.length > 0) hasText = true;
    if (textNode.commentThreadId !== threadId) return false;
  }

  return hasText;
};

export const findEmptyNewDraftThreadIds = (editor: Editor): string[] => {
  const threadIds = new Set<string>();
  Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => {
        if (Editor.isEditor(n) || !SlateElement.isElement(n)) return false;

        return Array.isArray((n as {comments?: RawComment[]}).comments);
      }
    })
  ).forEach(([node]) => {
    const list = (node as {comments?: RawComment[]}).comments ?? [];
    for (const c of list) {
      if (isEmptyNewDraft(c)) {
        threadIds.add(getThreadIdFromComment(c));
      }
    }
  });

  return Array.from(threadIds);
};

export const unsetThreadMarks = (editor: Editor, threadId: string): void => {
  Editor.withoutNormalizing(editor, () => {
    Transforms.unsetNodes(editor, 'commentThreadId', {
      at: [],
      match: (n) => Text.isText(n) && n.commentThreadId === threadId,
      mode: 'all'
    });
    Transforms.unsetNodes(editor, 'commentThreadResolved', {
      at: [],
      match: (n) => Text.isText(n) && n.commentThreadId === threadId,
      mode: 'all'
    });
  });
};

export const removeEmptyDraft = (editor: Editor, threadId: string): boolean => {
  const entry = findThreadBlockEntry(editor, threadId);
  if (!entry) return false;

  const [block, path] = entry;
  const existing = (block as {comments?: RawComment[]}).comments ?? [];
  const filtered = existing.filter(
    (c) => !(getThreadIdFromComment(c) === threadId && isEmptyNewDraft(c))
  );

  if (filtered.length === existing.length) return false;

  const threadComments = filtered.filter(
    (c) => getThreadIdFromComment(c) === threadId
  );

  if (threadComments.length === 0) {
    unsetThreadMarks(editor, threadId);
  }

  if (filtered.length === 0) {
    Transforms.setNodes(
      editor,
      {comments: undefined, commentId: undefined} as any,
      {at: path}
    );
  } else {
    Transforms.setNodes(editor, {comments: filtered} as any, {at: path});
  }

  return true;
};

export const removeAllEmptyNewDrafts = (editor: Editor): void => {
  const threadIds = findEmptyNewDraftThreadIds(editor);
  Editor.withoutNormalizing(editor, () => {
    threadIds.forEach((tid) => {
      removeEmptyDraft(editor, tid);
    });
  });
};

const clearStickyCommentMarks = (editor: Editor): void => {
  if (!editor.marks) return;

  const filteredEntries = Object.entries(editor.marks).filter(
    ([key]) => key !== 'commentThreadId' && key !== 'commentThreadResolved'
  );

  editor.marks =
    filteredEntries.length > 0 ? Object.fromEntries(filteredEntries) : null;
};

export const applyCommentThreadToRange = (
  editor: Editor,
  range: SlateRange,
  threadId: string
): void => {
  Editor.withoutNormalizing(editor, () => {
    Transforms.setNodes(
      editor,
      {commentThreadId: threadId},
      {
        at: range,
        match: (n) => Text.isText(n),
        split: true
      }
    );
    // Do not use Editor.removeMark here — with an expanded selection it
    // unsets commentThreadId from the range we just marked.
    clearStickyCommentMarks(editor);
  });
};

export const withComments = (editor: Editor): Editor => {
  const {insertBreak} = editor;

  editor.insertBreak = () => {
    clearStickyCommentMarks(editor);
    insertBreak();

    const {selection} = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [leaf, leafPath] = Editor.leaf(editor, selection);
      if (Text.isText(leaf) && leaf.text === '' && leaf.commentThreadId) {
        Transforms.unsetNodes(
          editor,
          ['commentThreadId', 'commentThreadResolved'],
          {at: leafPath}
        );
      }
    }
  };

  return editor;
};

/** Normalize legacy document fields to the Comment interface shape. */
export const normalizeComment = (
  raw: RawComment
): Comment & {mentions?: RawComment['mentions']} => {
  const firstName = String(raw.userFirstName ?? '').trim();
  const lastName = String(raw.userLastName ?? '').trim();

  const normalized: Comment & {mentions?: RawComment['mentions']} = {
    id: raw.id,
    threadId: raw.threadId,
    text: getCommentText(raw),
    userId: getCommentUserId(raw),
    userFirstName: firstName || undefined,
    userLastName: lastName || undefined,
    createdAt: raw.createdAt ?? new Date().toISOString(),
    updatedAt:
      raw.updatedAt ??
      raw.updateAt ??
      raw.createdAt ??
      new Date().toISOString(),
    type: raw.type,
    resolved: raw.resolved,
    isNew: raw.isNew,
    edited: raw.edited
  };
  if (raw.mentions) {
    normalized.mentions = raw.mentions;
  }

  return normalized;
};

export const findThreadBlockEntry = (
  editor: Editor,
  threadId: string
): [SlateElement, number[]] | null => {
  const matches = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => {
        if (Editor.isEditor(n) || !SlateElement.isElement(n)) return false;
        const list = (n as {comments?: RawComment[]}).comments;

        return (
          Array.isArray(list) &&
          list.some((c) => c.threadId === threadId || c.id === threadId)
        );
      }
    })
  );

  return matches.length > 0 ? (matches[0] as [SlateElement, number[]]) : null;
};

export const formatExactTime = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  return (
    date.toLocaleDateString() +
    ' ' +
    date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
  );
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;

  return formatExactTime(dateString);
};

export const scrollToCommentThread = (threadId: string): boolean => {
  const span = document.querySelector(
    `span[data-thread-id="${CSS.escape(threadId)}"]`
  );
  if (!span) return false;

  span.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
  const editorPanel = document.querySelector('.editor-panel');
  if (editorPanel instanceof HTMLElement) {
    const panelRect = editorPanel.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();
    if (spanRect.top < panelRect.top || spanRect.bottom > panelRect.bottom) {
      const offset =
        spanRect.top -
        panelRect.top +
        editorPanel.scrollTop -
        editorPanel.clientHeight / 2 +
        spanRect.height / 2;
      editorPanel.scrollTo({top: Math.max(0, offset), behavior: 'smooth'});
    }
  }

  return true;
};

export const extractThreadsFromContent = (
  content: any[],
  filter: 'all' | 'open' | 'resolved' = 'all',
  sort: 'newest' | 'oldest' = 'newest'
): CommentThread[] => {
  const extracted: RawComment[] = [];
  const traverse = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node.comments && Array.isArray(node.comments)) {
        extracted.push(...node.comments);
      }
      if (node.children) traverse(node.children);
    });
  };
  traverse(content);

  const threadsMap: Record<string, RawComment[]> = {};
  extracted.forEach((comment) => {
    const tid = comment.threadId ?? comment.id;
    if (!threadsMap[tid]) threadsMap[tid] = [];
    threadsMap[tid].push(comment);
  });

  const snippetMap: Record<string, string> = {};
  const traverseLeaves = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (typeof node.text === 'string' && node.commentThreadId) {
        snippetMap[node.commentThreadId] =
          (snippetMap[node.commentThreadId] || '') + node.text;
      }
      if (node.children) traverseLeaves(node.children);
    });
  };
  traverseLeaves(content);

  return Object.entries(threadsMap)
    .map(([tid, comments]): CommentThread => {
      const normalized = comments.map(normalizeComment);
      normalized.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      return {
        threadId: tid,
        root: normalized[0],
        replies: normalized.slice(1),
        snippet: (snippetMap[tid] || '').trim(),
        resolved: normalized.some((c) => c.resolved)
      };
    })
    .filter((t) => {
      if (filter === 'open') return !t.resolved;
      if (filter === 'resolved') return t.resolved;

      return true;
    })
    .sort((a, b) => {
      const ta = new Date(a.root.createdAt).getTime();
      const tb = new Date(b.root.createdAt).getTime();

      return sort === 'oldest' ? ta - tb : tb - ta;
    });
};

export const truncateSnippet = (text: string, maxLen = 80): string => {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= maxLen) return trimmed;

  return `${trimmed.slice(0, maxLen)}…`;
};
